import { Calendar, Truck, Info, Cube, Equals, TrendUp } from "@phosphor-icons/react"
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "~/components/reusable/hovercard/HoverCard"
import { formatNumber } from "~/utils/formatting"
import { DateFormatType, formatDate } from "~/utils/datetime"
import HoverCardRow from "../reusable/hovercard/HoverCardRow"
import HoverCardRowWrapper from "../reusable/hovercard/HoverCardRowWrapper"
import { ExpectedReturnsRow } from "./ExpectedReturnsRow"

interface Props {
  projectedStock: number
  expectedArrivalDate: Date
  currentStockLevel: number
  incomingStock: number
  expectedReturns?: number
  demandInPeriod: number
  isAggregated?: boolean
  /**
   * The date has passed, so the figure is a recorded reading rather than a projection. The
   * stock/incoming/demand terms describe a forward projection and do not add up to a recorded
   * value, so the breakdown collapses to the reading and its date (PRO-1364).
   */
  isRecorded?: boolean
}

export function ProjectedStockEADField(props: Props) {
  const { projectedStock } = props
  return (
    <HoverCard>
      <HoverCardTrigger className="flex h-full w-full items-center">
        {formatNumber(projectedStock)}
      </HoverCardTrigger>

      <HoverCardContent className="w-80 p-0" align="start">
        <ProjectedStockEADFieldContent {...props} />
      </HoverCardContent>
    </HoverCard>
  )
}

export function ProjectedStockEADFieldContent({
  projectedStock,
  expectedArrivalDate,
  currentStockLevel,
  incomingStock,
  expectedReturns = 0,
  demandInPeriod,
  isAggregated,
  isRecorded,
}: Props) {
  return (
    <>
      <div className="px-4 py-3 border-b border-base bg-base">
        <div className="flex items-center justify-between">
          <span className="label-base text-fg-base">
            {isRecorded ? "Recorded Stock Level" : "Stock Before Order Arrival"}
          </span>
        </div>
        <div className="text-2xl font-semibold mt-1 text-fg-base">
          {formatNumber(projectedStock)} units
          {isAggregated && <span className="paragraph-sm text-fg-subtle ml-1">(total)</span>}
        </div>
        <p className="paragraph-sm text-fg-subtle mt-1">
          {isRecorded
            ? "Stock level as recorded on this date"
            : "Stock level just before this order arrives"}
        </p>
      </div>

      <HoverCardRowWrapper>
        <HoverCardRow
          icon={<Calendar />}
          label={isRecorded ? "Date" : "Expected arrival"}
          value={formatDate(expectedArrivalDate, DateFormatType.FullDate)}
        />

        {!isRecorded && (
          <>
            <hr className="border-t border-dashed border-base" />
            <HoverCardRow
              icon={<Cube />}
              label="Current stock"
              value={`${formatNumber(currentStockLevel)} units`}
            />
            {incomingStock > 0 && (
              <HoverCardRow
                icon={<Truck />}
                label="Incoming orders"
                value={`+${formatNumber(incomingStock)} units`}
                className="text-green-500"
              />
            )}
            <ExpectedReturnsRow quantity={expectedReturns} />
            <HoverCardRow
              icon={<TrendUp />}
              label="Demand before arrival"
              value={`-${formatNumber(demandInPeriod)} units`}
              className="text-red-500"
            />
            <hr className="border-t border-dashed border-base" />
            <HoverCardRow
              icon={<Equals />}
              label="Projected stock"
              value={`${formatNumber(projectedStock)} units`}
            />
          </>
        )}
      </HoverCardRowWrapper>

      <div className="px-4 py-3 border-t border-base bg-subtle flex items-start gap-2 text-fg-subtle">
        <Info className="shrink-0" />
        <p className="paragraph-sm">
          {isRecorded
            ? "Measured stock level, not a projection"
            : "Projected stock when order arrives"}
        </p>
      </div>
    </>
  )
}
